class MrShopCarousel extends HTMLElement {
	// MARK: properties.

	#currentIndex = 0;

	#isTabletQuery = window.matchMedia( '(min-width: 768px)' );

	#isDesktopQuery = window.matchMedia( '(min-width: 1440px)' );

	#clickHandler = ( e: MouseEvent ): void => {
		// Ignore clicks with modifier keys: shift, ctrl, alt,...
		if ( e.metaKey ) {
			return;
		}

		// Check if target exist and is instance of HTMLElement.
		if ( !e.target || !( e.target instanceof HTMLElement ) ) {
			return;
		}

		// Unknown trigger, not handling this event.
		if (
			!e.target.hasAttribute( 'data-carousel-next' ) &&
			!e.target.hasAttribute( 'data-carousel-previous' )
		) {
			return;
		}

		e.preventDefault();
		e.stopPropagation();

		e.target.setAttribute( 'clicked', '' );

		setTimeout( () => {
			if ( e.target instanceof HTMLElement ) {
				e.target.removeAttribute( 'clicked' );
			}
		}, 512 );

		if ( e.target.hasAttribute( 'data-carousel-next' ) ) {
			this.goToNextItem();

			return;
		}

		if ( e.target.hasAttribute( 'data-carousel-previous' ) ) {
			this.goToPreviousItem();

			return;
		}
	};

	#resizeHandler = (): void => {
		requestAnimationFrame( () => {
			const trackEl = this.querySelector( 'ul[data-carousel-track]' ) as HTMLElement | null;
			if ( trackEl ) {
				trackEl.style.transform = 'translateX(0px)';
			}

			this.#currentIndex = 0;
			this.initButtons();
		} );
	};

	// MARK: lifecycle.
	connectedCallback() {
		this.addEventListener( 'click', this.#clickHandler );
		this.#isTabletQuery.addEventListener( 'change', this.#resizeHandler );
		this.#isDesktopQuery.addEventListener( 'change', this.#resizeHandler );

		this.initButtons();
	}

	disconnectedCallback() {
		this.removeEventListener( 'click', this.#clickHandler );
		this.#isTabletQuery.removeEventListener( 'change', this.#resizeHandler );
		this.#isDesktopQuery.removeEventListener( 'change', this.#resizeHandler );

		// Reset States.
		this.#currentIndex = 0;
	}

	get #translationDistance() {
		const offsetEl = this.querySelector( 'div[data-carousel-inner]' ) as HTMLElement | null;
		if ( !offsetEl ) {
			return 0;
		}

		return offsetEl.offsetWidth;
	}

	get #maxIndex() {
		const totalItems = this.getAttribute( 'item-count' );
		if ( !totalItems ) {
			return 0;
		}

		const totalItemsInt = parseInt( totalItems, 10 );
		if ( Number.isNaN( totalItemsInt ) ) {
			return 0;
		}

		if ( this.#isDesktopQuery.matches ) {
			return Math.ceil( totalItemsInt / 4 ) - 1;
		}

		if ( this.#isTabletQuery.matches ) {
			return Math.ceil( totalItemsInt / 3 ) - 1;
		}

		return Math.ceil( totalItemsInt / 2 ) - 1;
	}

	private initButtons() {
		const prevButton = this.querySelector( '.shop-carousel__button--previous' ) as Element | null;
		const nextButton = this.querySelector( '.shop-carousel__button--next' ) as Element | null;

		const controls = this.querySelector( '[data-carousel-controls]' ) as Element | null;

		if ( this.#maxIndex === 0 ) {
			prevButton?.setAttribute( 'disabled', '' );
			nextButton?.setAttribute( 'disabled', '' );

			controls?.setAttribute( 'hidden', 'hidden' );
		} else {
			prevButton?.setAttribute( 'disabled', '' );
			nextButton?.removeAttribute( 'disabled' );

			controls?.removeAttribute( 'hidden' );
		}
	}

	// MARK: methods.
	private goToPreviousItem() {
		if ( this.#currentIndex === 0 ) {
			return;
		}

		const track = this.querySelector( 'ul[data-carousel-track]' ) as HTMLElement | null;
		if ( !track ) {
			return;
		}

		const prevButton = this.querySelector( '.shop-carousel__button--previous' ) as Element | null;
		const nextButton = this.querySelector( '.shop-carousel__button--next' ) as Element | null;

		if ( this.#currentIndex === 1 ) {
			prevButton?.setAttribute( 'disabled', '' );
		}

		if ( this.#currentIndex !== this.#maxIndex - 1 ) {
			nextButton?.removeAttribute( 'disabled' );
		}

		this.#currentIndex = this.#currentIndex - 1;

		const translationDistanceForSlide = this.#currentIndex * this.#translationDistance;
		track.style.transform = `translateX(-${translationDistanceForSlide}px)`;
	}

	private goToNextItem() {
		if ( this.#currentIndex === this.#maxIndex ) {
			return;
		}

		const track = this.querySelector( 'ul[data-carousel-track]' ) as HTMLElement | null;
		if ( !track ) {
			return;
		}

		const prevButton = this.querySelector( '.shop-carousel__button--previous' ) as Element | null;
		const nextButton = this.querySelector( '.shop-carousel__button--next' ) as Element | null;

		if ( this.#currentIndex === this.#maxIndex - 1 ) {
			nextButton?.setAttribute( 'disabled', '' );
		}

		// Why is this parallel to previous button?
		prevButton?.removeAttribute( 'disabled' );

		this.#currentIndex = this.#currentIndex + 1;

		const translationDistanceForSlide = this.#currentIndex * this.#translationDistance;
		track.style.transform = `translateX(-${translationDistanceForSlide}px)`;
	}
}

customElements.define( 'mr-shop-carousel', MrShopCarousel );
